import './index.scss'
import currencies from '../../../data/currencies.json'
import {SimpleBlueButton} from '../../core/button'
import {useEffect, useState} from 'react'
import classNames from 'classnames'
import AmountManager from '../../../utils/amount'

const QUICK_SELECT = [50, 100, 500, 1000, 2000, 5000]

const getTotalPrice = (list, defaultCurrency, interval) => !list?.length ? 0 :
    list.reduce((sum, a) => sum + a.quantity * (AmountManager.normalizePrice(a.prices?.[defaultCurrency]?.[interval]?.price ?? 0)), 0)

const AddOnModalContent = ({
    addOnList,
    draftList,
    setDraftList,
    initialTabKey,
    setAddOnList,
    onCancel,
    onCloseModal,
    defaultCurrency,
    text,
    interval,
    isDarkMode
}) => {
    const [activeTab, setActiveTab] = useState(null)
    const [addOnsTotalPrice, setAddOnsTotalPrice] = useState(getTotalPrice(addOnList, defaultCurrency, interval))

    useEffect(() => {
        const initialTab = initialTabKey && addOnList?.find((a) => a.key === initialTabKey)
        setActiveTab(initialTab || addOnList[0])
    }, [interval, initialTabKey])

    useEffect(() => {
        if (activeTab && draftList?.length) {
            const updated = draftList.find((a) => a.key === activeTab.key)
            if (updated) setActiveTab(updated)
        }
    }, [draftList])

    const qty = activeTab?.quantity
    const pricePerUnit = parseFloat(activeTab?.prices?.[defaultCurrency]?.[interval]?.price) || 0
    const totalPrice = qty * pricePerUnit
    const pct = Math.min((qty / activeTab?.max) * 100, 100) || 0.5

    const handleChangeTab = (key) => {
        const tab = draftList?.find((a) => a.key === key)
        if (tab) setActiveTab(tab)
    }

    const updateDraftQuantity = (key, newQty) => {
        const updated = draftList?.map((a) => a.key === key ? {...a, quantity: newQty} : a)
        setDraftList(updated)
        setAddOnsTotalPrice(getTotalPrice(updated, defaultCurrency, interval))
    }

    const handleIncrement = (key) => {
        const addon = draftList?.find((a) => a.key === key)
        if (!addon) return
        updateDraftQuantity(key, Math.min(addon.max, addon.quantity + addon.step))
    }

    const handleDecrement = (key) => {
        const addon = draftList?.find((a) => a.key === key)
        if (!addon) return
        updateDraftQuantity(key, Math.max(addon.min_quantity, addon.quantity - addon.step))
    }

    const handleInputChange = (key, value) => {
        const addon = draftList?.find((a) => a.key === key)
        if (!addon) return
        const parsed = parseInt(value, 10)
        if (isNaN(parsed)) return
        const clamped = Math.min(addon.max, Math.max(addon.min_quantity, parsed))
        updateDraftQuantity(key, clamped)
    }

    const handleSliderChange = (key, value) => {
        const addon = draftList?.find((a) => a.key === key)
        if (!addon) return
        const snapped = Math.round(value / addon.step) * addon.step
        const clamped = Math.min(addon.max, Math.max(addon.min_quantity, snapped))
        updateDraftQuantity(key, clamped)
    }

    const handleQuickSelect = (key, value) => {
        const addon = draftList?.find((a) => a.key === key)
        if (!addon) return
        const clamped = Math.min(addon.max, Math.max(addon.min_quantity, value))
        updateDraftQuantity(key, clamped)
    }

    const handleSave = () => {
        setAddOnList(draftList)
        onCloseModal()
    }

    const isCancelBtnDisabled = draftList?.every(addOn => addOn?.quantity === 0)

    return (
        <div className="addon-modal-body">

            <p className={classNames('modal-description', {dark: isDarkMode})}>
                {text.add_ons_description}
            </p>

            <div className="addon-modal-main">
                <div className={classNames('addon-left-tabs', {dark: isDarkMode})}>
                    {draftList.map((tab) => (
                        <button
                            key={tab.key}
                            className={classNames('left-tab', {
                                dark: isDarkMode,
                                'left-tab-active': activeTab?.key === tab.key
                            })}
                            onClick={() => handleChangeTab(tab.key)}>
                            <span className="left-tab-title">{tab.tab_title}</span>
                            {tab.quantity > 0 && (
                                <span className="tab-badge">{tab.quantity}</span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="addon-right-content">
                    <div className='addon-header'>
                        <span className={classNames('addon-details', {dark: isDarkMode})}>
                            <p className='title'>{activeTab?.title}</p>
                            <p className="subtitle" title={activeTab?.description}>
                                {activeTab?.description}
                            </p>
                        </span>

                        <div className="addon-price">
                            <span className="price-amount">{AmountManager.normalizePrice(totalPrice.toFixed(2), currencies?.[defaultCurrency])}</span>
                            <span className="price-per-unit" title={AmountManager.normalizePrice(pricePerUnit.toFixed(2), currencies?.[defaultCurrency])}>
                                {AmountManager.normalizePrice(pricePerUnit.toFixed(2), currencies?.[defaultCurrency])} {activeTab?.per_month}
                            </span>
                        </div>
                    </div>

                    <div className="addon-quantity-row">
                        <div className={classNames('addon-spinner', {dark: isDarkMode})}>
                            <button
                                className={classNames('spinner-btn', {dark: isDarkMode})}
                                onClick={() => handleDecrement(activeTab?.key)}>
                                −
                            </button>
                            <input
                                className={classNames('spinner-input', {dark: isDarkMode})}
                                type="number"
                                min={activeTab?.min_quantity}
                                max={activeTab?.max}
                                value={qty ?? ''}
                                onChange={(e) => handleInputChange(activeTab?.key, e.target.value)}
                            />
                            <button
                                className={classNames('spinner-btn', {dark: isDarkMode})}
                                onClick={() => handleIncrement(activeTab?.key)}>
                                +
                            </button>
                        </div>

                        <div className="quick-select-pills">
                            {QUICK_SELECT
                                .filter(v => v >= (activeTab?.min_quantity ?? 0) && v <= (activeTab?.max ?? Infinity))
                                .map((v) => (
                                    <button
                                        key={v}
                                        className={classNames('quick-pill', {
                                            dark: isDarkMode,
                                            'active': qty === v
                                        })}
                                        onClick={() => handleQuickSelect(activeTab?.key, v)}>
                                        {v >= 1000 ? `${v / 1000}k` : v}
                                    </button>
                                ))}
                        </div>
                    </div>

                    <div className="addon-slider">
                        <div className={classNames('slider-track', {dark: isDarkMode})}>
                            <div className="slider-fill" style={{width: `${pct}%`}} />
                            <input
                                type="range"
                                min={activeTab?.min_quantity}
                                max={activeTab?.max}
                                step={activeTab?.step}
                                value={qty}
                                onChange={(e) => handleSliderChange(activeTab?.key, Number(e.target.value))}
                                className="slider-input"
                            />
                            <div className="slider-thumb" style={{left: `${pct}%`}} />
                        </div>
                        <div className="slider-labels">
                            <span>{activeTab?.min_quantity}</span>
                            <span>{activeTab?.max}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className={classNames('modal-bottom', {dark: isDarkMode})}>
                <span className='price-details'>
                    <span className="price-title">{text.total_add_ons}</span>
                    <span className="total-price">+{currencies?.[defaultCurrency]}{addOnsTotalPrice.toFixed(2)}</span>
                </span>

                <div className="modal-buttons">
                    <SimpleBlueButton className="cancel-button" onClick={onCancel} disabled={isCancelBtnDisabled}>
                        {text.cancel}
                    </SimpleBlueButton>
                    <SimpleBlueButton onClick={handleSave}>
                        {text.save}
                    </SimpleBlueButton>
                </div>
            </div>
        </div>
    )
}

export default AddOnModalContent
